import React, { HTMLAttributes, forwardRef } from 'react'

import clsx from 'clsx'

export interface LoaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The tone of the loader pulse background
   */
  color?: 'default'
  /**
   * Should the loader animate
   */
  pulse?: boolean
  /**
   * Should the loader be circular
   */
  rounded?: boolean
  /**
   * Should the loader take up the width of its container
   */
  full?: boolean
  /** Should the loader be split into bars, accepts a number of rows or array of classNames applied to each row */
  bars?: number | Array<string>
}

const LoaderStyle = {
  _: ' p-1',
  bars: 'flex flex-col items-stretch space-y-2',
  color: {
    // TODO: move to tailwind config?
    default: 'bg-gradient-to-r from-[#535A61] to-[#3B4046]'
  },
  full: 'w-full',
  pulse: 'animate-pulse',
  rounded: ['rounded-sm', 'rounded-full']
}
/**
 * Spinners provide a visual cue that an action is either processing, awaiting a course of change or a result.
 * This spinner will inherit the current border color, and will by default be sized at 1rem but can be overridden with w- and h- classes.
 *
 */
export const Loader = forwardRef<HTMLDivElement, LoaderProps>(function Loader(
  { bars, className, color = 'default', full = false, pulse, rounded = false, ...props },
  ref
) {
  const LoaderBody = bars ? (
    <div
      className={clsx(className, full && LoaderStyle.full, LoaderStyle.bars)}
      data-testid='loader'
      ref={ref}
      {...props}
    >
      {(typeof bars === 'number' ? Array.from({ length: bars }, () => '') : bars).map((b, i) => (
        <div
          className={clsx(
            b,
            LoaderStyle._,
            pulse && LoaderStyle.pulse,
            LoaderStyle.color[color],
            LoaderStyle.rounded[~~rounded]
          )}
          key={b + i}
        >
          &nbsp;
        </div>
      ))}
    </div>
  ) : (
    <div
      className={clsx(
        className,
        LoaderStyle._,
        pulse && LoaderStyle.pulse,
        LoaderStyle.color[color],
        LoaderStyle.rounded[~~rounded],
        full && LoaderStyle.full
      )}
      data-testid='loader'
      ref={ref}
      {...props}
    >
      &nbsp;
    </div>
  )

  return LoaderBody
})
